import { useEffect, useState } from "react";
import api from "../lib/api";
import { Sparkles, Calendar, Utensils, BarChart3, Ticket } from "lucide-react";

const todayISO = () => new Date().toISOString().slice(0, 10);

export default function AdminDashboard() {
  const [stats, setStats] = useState({ activeOffer: null, reservations: [], menu: 0, events: 0 });
  const [date, setDate] = useState(todayISO());
  const [timeline, setTimeline] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/offers/active").then((r) => r.data).catch(() => null),
      api.get("/reservations").then((r) => r.data).catch(() => []),
      api.get("/menu").then((r) => r.data).catch(() => []),
      api.get("/events/all").then((r) => r.data).catch(() => []),
    ]).then(([offer, res, menu, events]) => setStats({ activeOffer: offer, reservations: res, menu: menu.length, events: events.length }));
  }, []);

  useEffect(() => {
    api.get(`/occupancy/${date}`).then((r) => setTimeline(r.data)).catch(() => setTimeline(null));
  }, [date]);

  const upcoming = stats.reservations.filter((r) => {
    const d = new Date(`${r.date}T${r.time || "00:00"}`);
    return !isNaN(d) && d >= new Date();
  });

  return (
    <div data-testid="admin-dashboard">
      <h1 className="font-serif-display text-4xl text-[#5979A9] mb-2">Dashboard</h1>
      <p className="text-[#4A5568] mb-8">Quick overview of what's happening at Himalayan Cafe.</p>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
        <StatCard icon={<Sparkles/>} label="Active Offer" value={stats.activeOffer ? stats.activeOffer.title : "None"} sub={stats.activeOffer ? stats.activeOffer.price : "Create one in Offers"}/>
        <StatCard icon={<Calendar/>} label="Reservations" value={stats.reservations.length} sub={`${upcoming.length} upcoming`}/>
        <StatCard icon={<Ticket/>} label="Events" value={stats.events} sub="Total scheduled"/>
        <StatCard icon={<Utensils/>} label="Menu Items" value={stats.menu} sub="Live on website"/>
      </div>

      <div className="bg-white border border-[#F0EAE6] rounded-2xl p-6 mb-10">
        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
          <h2 className="font-serif-display text-2xl text-[#5979A9] flex items-center gap-2"><BarChart3 size={20}/> Occupancy Timeline</h2>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="timeline-date"
                 className="bg-transparent border-b-2 border-[#F0EAE6] focus:border-[#E05947] outline-none py-1.5 text-sm"/>
        </div>
        {!timeline || timeline.slots.length === 0 ? (
          <p className="text-sm text-[#4A5568]">No slots on this date (cafe closed).</p>
        ) : (
          <>
            <div className="text-xs text-[#4A5568] mb-3">Capacity: <strong>{timeline.max_capacity}</strong> · Reservations: <strong>{timeline.reservations.length}</strong></div>
            <div className="space-y-1.5">
              {timeline.slots.map((slot) => {
                const pct = Math.round(((timeline.max_capacity - slot.remaining) / timeline.max_capacity) * 100);
                const color = slot.status === "full" ? "bg-[#E05947]" : slot.status === "limited" ? "bg-yellow-400" : "bg-green-500";
                return (
                  <div key={slot.time} className="flex items-center gap-3 text-xs">
                    <span className="w-12 text-[#5979A9] font-mono">{slot.time}</span>
                    <div className="flex-1 h-5 bg-[#FAF4F1] rounded-full overflow-hidden relative">
                      <div className={`absolute inset-y-0 left-0 ${color} transition-all`} style={{ width: `${pct}%` }}/>
                    </div>
                    <span className="w-24 text-right text-[#4A5568]">{timeline.max_capacity - slot.remaining}/{timeline.max_capacity} · {pct}%</span>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      <h2 className="font-serif-display text-2xl text-[#5979A9] mb-4">Upcoming Bookings</h2>
      <div className="bg-white border border-[#F0EAE6] rounded-2xl overflow-hidden">
        {upcoming.length === 0 ? (
          <p className="p-6 text-sm text-[#4A5568]">No upcoming reservations yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#FAF4F1] text-[#5979A9]">
              <tr>
                <th className="text-left px-5 py-3">Name</th>
                <th className="text-left px-5 py-3">Phone</th>
                <th className="text-left px-5 py-3">Date</th>
                <th className="text-left px-5 py-3">Time</th>
                <th className="text-left px-5 py-3">Guests</th>
              </tr>
            </thead>
            <tbody>
              {upcoming.slice(0, 6).map((r) => (
                <tr key={r.id} className="border-t border-[#F0EAE6]">
                  <td className="px-5 py-3 font-medium">{r.full_name}</td>
                  <td className="px-5 py-3">{r.phone}</td>
                  <td className="px-5 py-3">{r.date}</td>
                  <td className="px-5 py-3">{r.time}</td>
                  <td className="px-5 py-3">{r.guests}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, sub }) {
  return (
    <div className="bg-white p-6 rounded-2xl border border-[#F0EAE6] shadow-sm">
      <div className="flex items-center gap-3 text-[#E05947] mb-3">{icon}<span className="text-xs uppercase tracking-[0.2em] text-[#5979A9]/80 font-semibold">{label}</span></div>
      <div className="font-serif-display text-3xl text-[#5979A9] leading-tight">{value}</div>
      {sub && <div className="text-sm text-[#4A5568] mt-1">{sub}</div>}
    </div>
  );
}
